// ToOutsource.jsx - With Currency Context, View Tracking, Month Filter, Search, and PDF/Excel Export
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { FaEye, FaList, FaCheckCircle, FaClock, FaSync, FaCheck, FaPlay, FaTimes, FaCircle, FaSearch, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import Footer from '../../../footer.js';
import { Button, Spinner, Modal } from 'react-bootstrap';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../../context/currencyContext.js';
import usePricingAccess from '../../../../hooks/usePricingAccess.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './ToOutsource.css';

const ToOutsource = () => {
  const { currency, formatAmount } = useCurrency();

  // Pricing access — hides amounts & disables invoice links when denied
  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  const [outsourceCases, setOutsourceCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLabId, setUserLabId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const itemsPerPage = 10;

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isMonthFilterActive, setIsMonthFilterActive] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [showProceduresModal, setShowProceduresModal] = useState(false);
  const [selectedCaseProcedures, setSelectedCaseProcedures] = useState(null);
  const [viewInfoMap, setViewInfoMap] = useState({});

  const [currentLabInfo, setCurrentLabInfo] = useState({
    labName: '',
    address: '',
    phone: '',
    email: '',
    labId: ''
  });

  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    totalPendingAmount: 0,
    totalInProgressAmount: 0,
    totalCompletedAmount: 0,
    totalAllAmount: 0
  });

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const loadUserInfo = () => {
      let storedLabId = localStorage.getItem('userLabId');
      if (!storedLabId) storedLabId = sessionStorage.getItem('userLabId');

      const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
      let userData = null;
      if (userDataStr) {
        try {
          userData = JSON.parse(userDataStr);
          if (userData.labId && !storedLabId) storedLabId = userData.labId;
        } catch (e) {
          console.error('Error parsing userData:', e);
        }
      }

      if (storedLabId) {
        const formattedLabId = String(storedLabId).padStart(8, '0');
        setUserLabId(formattedLabId);
      }

      setCurrentLabInfo({
        labName: userData?.labName || userData?.name || localStorage.getItem('labName') || 'Your Laboratory',
        address: userData?.address || userData?.labAddress || localStorage.getItem('labAddress') || '',
        phone: userData?.phone || userData?.labPhone || localStorage.getItem('labPhone') || '',
        email: userData?.email || userData?.labEmail || localStorage.getItem('labEmail') || '',
        labId: storedLabId || ''
      });
    };
    loadUserInfo();
  }, []);

  const getUserInfo = useCallback(() => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
    return { token };
  }, []);

  const getProceduresArray = useCallback((caseItem) => {
    if (!caseItem) return [];
    if (caseItem.selectedProcedures && Array.isArray(caseItem.selectedProcedures) && caseItem.selectedProcedures.length > 0) {
      return caseItem.selectedProcedures;
    }
    if (caseItem.selectedProcedure && Object.keys(caseItem.selectedProcedure).length > 0) {
      return [caseItem.selectedProcedure];
    }
    return [];
  }, []);

  const calculateCaseTotal = useCallback((caseItem) => {
    const procedures = getProceduresArray(caseItem);
    return procedures.reduce((sum, proc) => {
      if (proc.totalPrice !== undefined && proc.totalPrice !== null) {
        return sum + (parseFloat(proc.totalPrice) || 0);
      }
      const price = proc.price || 0;
      const quantity = proc.quantity || 1;
      return sum + (parseFloat(price) * parseFloat(quantity) || 0);
    }, 0);
  }, [getProceduresArray]);

  const calculateStatusCounts = useCallback((cases, currentViewInfoMap = {}) => {
    const counts = {
      pending: 0,
      inProgress: 0,
      completed: 0,
      totalPendingAmount: 0,
      totalInProgressAmount: 0,
      totalCompletedAmount: 0,
      totalAllAmount: 0
    };

    cases.forEach(caseItem => {
      const viewInfo = currentViewInfoMap[caseItem.caseNumber];
      const authoritativeStatus = viewInfo?.status || caseItem.status;

      if (authoritativeStatus === 'cancelled') return;

      const caseTotal = calculateCaseTotal(caseItem);
      counts.totalAllAmount += caseTotal;

      if (authoritativeStatus === 'completed') {
        counts.completed++;
        counts.totalCompletedAmount += caseTotal;
        return;
      }

      if (authoritativeStatus === 'accepted' || authoritativeStatus === 'in_progress') {
        counts.inProgress++;
        counts.totalInProgressAmount += caseTotal;
        return;
      }

      if (authoritativeStatus === 'pending') {
        if (!viewInfo?.viewedByDestination) {
          counts.pending++;
          counts.totalPendingAmount += caseTotal;
        } else {
          counts.inProgress++;
          counts.totalInProgressAmount += caseTotal;
        }
      }
    });

    return counts;
  }, [calculateCaseTotal]);

  const fetchViewInfoForCasesBatch = useCallback(async (cases, signal) => {
    try {
      const { token } = getUserInfo();
      const caseNumbers = cases.map(c => c.caseNumber).filter(Boolean);
      if (caseNumbers.length === 0) return;

      const params = new URLSearchParams();
      params.append('caseNumbers', caseNumbers.join(','));
      params.append('userLabId', userLabId);

      if (isMonthFilterActive) {
        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        params.append('startDate', startOfMonth.toISOString());
        params.append('endDate', endOfMonth.toISOString());
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/view-stats-bulk`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true,
          signal
        }
      );

      if (!isMountedRef.current) return;

      if (response.data?.success) {
        const statsMap = response.data.data || {};
        const newViewMap = {};
        Object.keys(statsMap).forEach(caseNumber => {
          const stats = statsMap[caseNumber];
          newViewMap[caseNumber] = {
            viewedByDestination: !!stats.destinationLabView,
            firstViewedAt: stats.firstViewedByDestination,
            isDestinationLab: stats.isDestinationLab || false,
            isSourceLab: stats.isSourceLab || false,
            status: stats.status || null,
            hasInvoice: !!stats.hasInvoice,
            invoiceNumber: stats.invoiceNumber || null
          };
        });

        setViewInfoMap(prev => ({ ...prev, ...newViewMap }));

        setOutsourceCases(prev =>
          prev.map(c => {
            const fresh = newViewMap[c.caseNumber];
            if (!fresh) return c;
            const updates = {};
            if (fresh.status && fresh.status !== c.status) {
              updates.status = fresh.status;
            }
            if (fresh.hasInvoice && !c.invoiceNumber && fresh.invoiceNumber) {
              updates.invoiceNumber = fresh.invoiceNumber;
            }
            return Object.keys(updates).length ? { ...c, ...updates } : c;
          })
        );
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching bulk view info for cases:', error);
    }
  }, [userLabId, getUserInfo, isMonthFilterActive, selectedMonth]);

  const fetchOutsourceCases = useCallback(async () => {
    if (!userLabId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('type', 'sent');

      if (isMonthFilterActive) {
        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        params.append('startDate', startOfMonth.toISOString());
        params.append('endDate', endOfMonth.toISOString());
      }

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      if (activeStatusFilter !== 'all' &&
          activeStatusFilter !== 'in_progress' &&
          activeStatusFilter !== 'pending') {
        params.append('status', activeStatusFilter);
      }

      params.append('page', '1');
      params.append('limit', '500');

      const { token } = getUserInfo();
      const url = `${API_BASE_URL}/api/outsource-case/lab/${userLabId}/cases?${params.toString()}`;

      const response = await axios.get(url, {
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'userLabId': userLabId,
          'x-user-lab-id': userLabId
        },
        withCredentials: true
      });

      if (!isMountedRef.current) return;

      if (response.data && response.data.success) {
        let cases = response.data.data || [];

        cases = cases.filter(caseItem =>
          caseItem.sourceLab?.userLabId === userLabId &&
          caseItem.status !== 'cancelled'
        );

        setViewInfoMap({});
        setOutsourceCases(cases);

        const initialCounts = calculateStatusCounts(cases, {});
        setStatusCounts(initialCounts);

        if (cases.length > 0) {
          fetchViewInfoForCasesBatch(cases, controller.signal);
        }
      } else {
        setError('Failed to fetch outsource cases');
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching outsource cases:', error);
      if (isMountedRef.current) {
        setError(error.response?.data?.message || 'Failed to fetch outsource cases');
        toast.error('Failed to load outsource cases');
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [
    userLabId,
    selectedMonth,
    isMonthFilterActive,
    debouncedSearch,
    activeStatusFilter,
    calculateStatusCounts,
    fetchViewInfoForCasesBatch,
    getUserInfo
  ]);

  useEffect(() => {
    if (!userLabId) return;
    fetchOutsourceCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, isMonthFilterActive, debouncedSearch, activeStatusFilter, userLabId]);

  useEffect(() => {
    if (outsourceCases.length > 0 && Object.keys(viewInfoMap).length > 0) {
      const updatedCounts = calculateStatusCounts(outsourceCases, viewInfoMap);
      setStatusCounts(updatedCounts);
    }
  }, [viewInfoMap, outsourceCases, calculateStatusCounts]);

  // ✅ FIX: single source of truth for "is this case cancelled?"
  // Uses the freshest status from the bulk endpoint, falls back to list status.
  const isCaseCancelled = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;
    return status === 'cancelled';
  }, [viewInfoMap]);

  const isCaseTrulyPending = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;
    if (status !== 'pending') return false;
    return !viewInfo?.viewedByDestination;
  }, [viewInfoMap]);

  const isCaseEffectivelyInProgress = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;
    if (status === 'in_progress' || status === 'accepted') return true;
    if (status === 'pending') {
      return viewInfo?.viewedByDestination === true;
    }
    return false;
  }, [viewInfoMap]);

  const isCaseUnread = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;

    if (status === 'completed' || status === 'cancelled') {
      return false;
    }
    if (!viewInfo) return false;
    return !viewInfo.viewedByDestination;
  }, [viewInfoMap]);

  const getEffectiveStatus = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const authoritativeStatus = viewInfo?.status || caseItem.status;

    if (authoritativeStatus === 'pending') {
      if (viewInfo?.viewedByDestination === true) {
        return 'in_progress';
      }
    }
    return authoritativeStatus;
  }, [viewInfoMap]);

  const filteredResults = useMemo(() => {
    if (!outsourceCases.length) return [];

    // ✅ FIX: always drop cancelled cases (using the freshest status) and
    // de-duplicate by _id so the table, the counts and the exports all agree.
    const seen = new Set();
    let filtered = outsourceCases.filter(c => {
      if (isCaseCancelled(c)) return false;
      const key = String(c._id || c.caseNumber);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (activeStatusFilter === 'in_progress') {
      filtered = filtered.filter(c => isCaseEffectivelyInProgress(c));
    } else if (activeStatusFilter === 'pending') {
      filtered = filtered.filter(c => isCaseTrulyPending(c));
    } else if (activeStatusFilter !== 'all') {
      filtered = filtered.filter(c => {
        const viewInfo = viewInfoMap[c.caseNumber];
        const status = viewInfo?.status || c.status;
        return status === activeStatusFilter;
      });
    }

    return filtered;
  }, [
    outsourceCases,
    viewInfoMap,
    activeStatusFilter,
    isCaseTrulyPending,
    isCaseEffectivelyInProgress,
    isCaseCancelled
  ]);

  useEffect(() => {
    setFilteredCases(filteredResults);
    setCurrentPage(1);
  }, [filteredResults]);

  const goToPreviousMonth = () => {
    setSelectedMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
    setIsMonthFilterActive(true);
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
    setIsMonthFilterActive(true);
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const resetMonthFilter = () => {
    setIsMonthFilterActive(false);
    setSelectedMonth(new Date());
    setActiveStatusFilter('all');
    setSearchTerm('');
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const handleRefresh = () => {
    setOutsourceCases([]);
    setViewInfoMap({});
    fetchOutsourceCases();
  };

  const handleStatusFilter = (status) => {
    setActiveStatusFilter(prev => prev === status ? 'all' : status);
  };

  const clearSearch = () => setSearchTerm('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateNumeric = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrencySymbol = useCallback(() => {
    return currency?.currencySymbol || '₮';
  }, [currency]);

  const formatCurrency = useCallback((amount) => {
    if (!hasPricingAccess) return null;

    if (amount === null || amount === undefined) return '0.00';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return '0.00';

    if (formatAmount) {
      return formatAmount(numericAmount);
    }
    return `${getCurrencySymbol()}${numericAmount.toFixed(2)}`;
  }, [formatAmount, getCurrencySymbol, hasPricingAccess]);

  const formatNumberPlain = (amount) => {
    if (amount === null || amount === undefined) return '0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleViewCase = (caseItem) => {
    window.location.href = `/case/${caseItem.caseNumber}`;
  };

  const handleViewOutsource = (caseItem) => {
    window.location.href = `/outsource-case/${caseItem.caseNumber}`;
  };

  const handleViewInvoice = (invoiceNumber, e) => {
    e.preventDefault();
    if (!hasPricingAccess) return;
    if (invoiceNumber) {
      window.location.href = `/outsource-invoice/${invoiceNumber}`;
    }
  };

  const handleShowProcedures = (caseItem) => {
    setSelectedCaseProcedures(caseItem);
    setShowProceduresModal(true);
  };

  const handleCloseProceduresModal = () => {
    setShowProceduresModal(false);
    setSelectedCaseProcedures(null);
  };

  const paginatedCases = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredCases.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredCases, currentPage]);

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  const renderViewStatus = (caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const unread = isCaseUnread(caseItem);

    if (!viewInfo) {
      return <span className="view-status-loading">Loading...</span>;
    }

    if (unread) {
      return (
        <div className="view-status-unread">
          <FaCircle className="view-status-unread-dot" />
          <span className="view-status-unread-badge">Not viewed yet</span>
        </div>
      );
    } else {
      const timeAgo = formatTimeAgo(viewInfo.firstViewedAt);
      return (
        <div className="view-status-read">
          <FaCheckCircle className="view-status-read-icon" />
          <span>Viewed {timeAgo}</span>
        </div>
      );
    }
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // ================================================================
  // EXPORT HELPERS
  // ================================================================

  // ✅ FIX: the exact list of cases that goes into the PDF/Excel.
  // Same list as the table (filteredCases), with a final guard that removes
  // any cancelled case and any duplicate, so export count === screen count.
  const getExportableCases = useCallback(() => {
    const seen = new Set();
    return filteredCases.filter(caseItem => {
      if (isCaseCancelled(caseItem)) return false;
      const key = String(caseItem._id || caseItem.caseNumber);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredCases, isCaseCancelled]);

  // ✅ FIX: "Sr No" now counts CASES, not procedure lines.
  // A case with 2+ procedures still prints one line per procedure, but only
  // the first line carries the Sr No / Case No — so the last Sr No equals the
  // number of cases shown on screen.
  const buildLineItems = (cases) => {
    const lines = [];

    cases.forEach((caseItem, caseIdx) => {
      const procedures = getProceduresArray(caseItem);
      const caseSrNo = caseIdx + 1;

      if (procedures.length > 0) {
        procedures.forEach((proc, procIdx) => {
          const price = parseFloat(proc.price) || 0;
          const qty = parseFloat(proc.quantity) || 1;
          const lineTotal = parseFloat(proc.totalPrice) || (price * qty);

          lines.push({
            srNo: procIdx === 0 ? caseSrNo : '',
            caseNumber: procIdx === 0 ? (caseItem.caseNumber || '') : '',
            description: proc.procedureName || '',
            unit: qty,
            unitPrice: price,
            total: lineTotal,
            sourceLab: caseItem.sourceLab?.labName || '',
            destinationLab: caseItem.destinationLab?.labName || '',
            createdAt: caseItem.createdAt,
            requiredDate: caseItem.requiredDate,
          });
        });
      } else {
        lines.push({
          srNo: caseSrNo,
          caseNumber: caseItem.caseNumber || '',
          description: 'No procedure',
          unit: 0,
          unitPrice: 0,
          total: 0,
          sourceLab: caseItem.sourceLab?.labName || '',
          destinationLab: caseItem.destinationLab?.labName || '',
          createdAt: caseItem.createdAt,
          requiredDate: caseItem.requiredDate,
        });
      }
    });

    return lines;
  };

  // ============ PDF EXPORT ============
  const exportToPDF = () => {
    const exportCases = getExportableCases();

    if (exportCases.length === 0) {
      toast.info('No cases to export.');
      return;
    }

    const lines = buildLineItems(exportCases);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const headerTop = 40;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#000000');
    doc.text(currentLabInfo.labName || 'Laboratory', marginX, headerTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let y = headerTop + 16;

    if (currentLabInfo.address) {
      const addressLines = doc.splitTextToSize(currentLabInfo.address, 240);
      doc.text(addressLines, marginX, y);
      y += addressLines.length * 12;
    }
    if (currentLabInfo.phone) {
      doc.text(currentLabInfo.phone, marginX, y);
      y += 12;
    }
    if (currentLabInfo.email) {
      doc.setTextColor('#1a56db');
      doc.text(currentLabInfo.email, marginX, y);
      doc.setTextColor('#000000');
      y += 12;
    }

    const rightX = pageWidth - marginX;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DATE', rightX, headerTop, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const todayForHeader = formatDateNumeric(new Date());
    doc.text(todayForHeader, rightX, headerTop + 14, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text('Bill of Month', rightX, headerTop + 32, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const billOfMonth = isMonthFilterActive
      ? selectedMonth.toLocaleDateString('en-US', { month: 'long' })
      : 'All Time';
    doc.text(billOfMonth, rightX, headerTop + 46, { align: 'right' });

    let billToY = headerTop + 70;
    doc.setFontSize(9);
    doc.setTextColor('#666666');
    doc.text('BILL FOR', marginX, billToY);
    doc.setTextColor('#000000');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);

    const uniqueDestLabs = [...new Set(exportCases.map(c => c.destinationLab?.labName).filter(Boolean))];
    const destLabel = uniqueDestLabs.length === 1
      ? uniqueDestLabs[0]
      : uniqueDestLabs.length > 1
        ? `${uniqueDestLabs.length} Destination Labs`
        : 'N/A';
    doc.text(destLabel, marginX, billToY + 16);

    const tableStartY = billToY + 40;

    const tableBody = hasPricingAccess
      ? lines.map((line) => [
          line.srNo,
          line.caseNumber,
          line.description,
          line.unit,
          formatNumberPlain(line.unitPrice),
          formatNumberPlain(line.total),
        ])
      : lines.map((line) => [
          line.srNo,
          line.caseNumber,
          line.description,
          line.unit,
        ]);

    const headRow = hasPricingAccess
      ? [['Sr No', 'Case No', 'Description', 'Unit', 'UNIT PRICE', 'TOTAL']]
      : [['Sr No', 'Case No', 'Description', 'Unit']];

    const columnStyles = hasPricingAccess
      ? {
          0: { halign: 'center', cellWidth: 40 },
          1: { halign: 'center', cellWidth: 55 },
          2: { halign: 'left', cellWidth: 'auto' },
          3: { halign: 'center', cellWidth: 40 },
          4: { halign: 'right', cellWidth: 65 },
          5: { halign: 'right', cellWidth: 65 },
        }
      : {
          0: { halign: 'center', cellWidth: 40 },
          1: { halign: 'center', cellWidth: 55 },
          2: { halign: 'left', cellWidth: 'auto' },
          3: { halign: 'center', cellWidth: 40 },
        };

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);

    autoTable(doc, {
      startY: tableStartY,
      head: headRow,
      body: tableBody,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 5,
        textColor: '#000000',
        lineColor: '#000000',
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [0, 74, 173],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 9,
      },
      columnStyles,
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: marginX, right: marginX },
      didDrawPage: () => {},
    });

    if (hasPricingAccess) {
      let finalY = doc.lastAutoTable.finalY + 10;
      const totalRowX = pageWidth - marginX;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('GRAND TOTAL', totalRowX - 90, finalY, { align: 'right' });
      doc.text(formatNumberPlain(grandTotal), totalRowX, finalY, { align: 'right' });
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor('#888888');
    doc.text(
      `Generated on ${new Date().toLocaleString()}  |  Total cases: ${exportCases.length}  |  Total line items: ${lines.length}`,
      marginX,
      pageHeight - 20
    );

    const periodSlug = isMonthFilterActive
      ? `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`
      : 'All_Time';
    doc.save(`Outsource_Cases_${periodSlug}.pdf`);
  };

  // ============ EXCEL EXPORT ============
  const exportToExcel = () => {
    const exportCases = getExportableCases();

    if (exportCases.length === 0) {
      toast.info('No cases to export.');
      return;
    }

    const lines = buildLineItems(exportCases);

    const todayForHeader = formatDateNumeric(new Date());
    const billOfMonth = isMonthFilterActive
      ? selectedMonth.toLocaleDateString('en-US', { month: 'long' })
      : 'All Time';
    const uniqueDestLabs = [...new Set(exportCases.map(c => c.destinationLab?.labName).filter(Boolean))];
    const destLabel = uniqueDestLabs.length === 1
      ? uniqueDestLabs[0]
      : uniqueDestLabs.length > 1
        ? `${uniqueDestLabs.length} Destination Labs`
        : 'N/A';

    const headerRows = [
      [currentLabInfo.labName || 'Laboratory'],
      [currentLabInfo.address || ''],
      [currentLabInfo.phone || ''],
      [currentLabInfo.email || ''],
      [],
      ['DATE', todayForHeader, '', 'Bill of Month', billOfMonth],
      ['BILL FOR', destLabel],
      [],
      hasPricingAccess
        ? ['Sr No', 'Case No', 'Description', 'Unit', 'UNIT PRICE', 'TOTAL']
        : ['Sr No', 'Case No', 'Description', 'Unit'],
    ];

    const bodyRows = hasPricingAccess
      ? lines.map((line) => [
          line.srNo,
          line.caseNumber,
          line.description,
          line.unit,
          line.unitPrice,
          line.total,
        ])
      : lines.map((line) => [
          line.srNo,
          line.caseNumber,
          line.description,
          line.unit,
        ]);

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);
    const footerRows = hasPricingAccess
      ? [
          [],
          ['', '', 'TOTAL CASES', exportCases.length, 'GRAND TOTAL', grandTotal],
        ]
      : [
          [],
          ['', '', 'TOTAL CASES', exportCases.length],
        ];

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...bodyRows, ...footerRows]);

    worksheet['!cols'] = hasPricingAccess
      ? [
          { wch: 8 },
          { wch: 14 },
          { wch: 40 },
          { wch: 10 },
          { wch: 14 },
          { wch: 14 },
        ]
      : [
          { wch: 8 },
          { wch: 14 },
          { wch: 40 },
          { wch: 10 },
        ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Outsource Cases');

    const periodSlug = isMonthFilterActive
      ? `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`
      : 'All_Time';
    XLSX.writeFile(workbook, `Outsource_Cases_${periodSlug}.xlsx`);
  };

  const statusCards = useMemo(() => [
    {
      key: 'pending',
      label: 'Pending Cases',
      count: statusCounts.pending,
      amount: statusCounts.totalPendingAmount,
      icon: <FaClock />,
      accent: '#d97706',
      tint: '#fffaf0',
      activeTint: '#fef3e2'
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      count: statusCounts.inProgress,
      amount: statusCounts.totalInProgressAmount,
      icon: <FaPlay />,
      accent: '#2563eb',
      tint: '#f5f8ff',
      activeTint: '#e8f0fe'
    },
    {
      key: 'completed',
      label: 'Completed',
      count: statusCounts.completed,
      amount: statusCounts.totalCompletedAmount,
      icon: <FaCheck />,
      accent: '#0f9d58',
      tint: '#f4fbf6',
      activeTint: '#e6f6ea'
    }
  ], [statusCounts]);

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'pending':
        return { color: '#856404', icon: <FaClock style={{ marginRight: '5px' }} /> };
      case 'accepted':
        return { color: '#084298', icon: <FaCheck style={{ marginRight: '5px' }} /> };
      case 'in_progress':
        return { color: '#0d6efd', icon: <FaPlay style={{ marginRight: '5px' }} /> };
      case 'completed':
        return { color: '#0f5132', icon: <FaCheck style={{ marginRight: '5px' }} /> };
      case 'cancelled':
        return { color: '#721c24', icon: <FaTimes style={{ marginRight: '5px' }} /> };
      default:
        return { color: '#6c757d', icon: null };
    }
  };

  const tableRows = useMemo(() => {
    return paginatedCases.map((caseItem) => {
      const procedures = getProceduresArray(caseItem);
      const procedureCount = procedures.length;
      const unread = isCaseUnread(caseItem);
      const effectiveStatus = getEffectiveStatus(caseItem);
      const isEffectivelyInProgress = isCaseEffectivelyInProgress(caseItem);
      const displayStatusStyle = getStatusBadgeStyle(effectiveStatus);

      const isReOutsource = !!caseItem.isReOutsource;

      const getStatusDisplayText = () => {
        const viewInfo = viewInfoMap[caseItem.caseNumber];
        const authoritativeStatus = viewInfo?.status || caseItem.status;
        if (authoritativeStatus === 'pending' && isEffectivelyInProgress) {
          return 'IN PROGRESS';
        }
        return (authoritativeStatus || 'PENDING').toUpperCase().replace('_', ' ');
      };

      const rowBackground = unread ? '#f0f7ff' : 'transparent';

      return (
        <tr
          key={caseItem._id}
          className={`outsource-row ${unread ? 'outsource-row-unread' : ''}`}
          style={{
            backgroundColor: rowBackground,
            fontWeight: unread ? 'bold' : 'normal'
          }}
        >
          <td className="case-number-cell">
            <div className="case-number-wrapper">
              <span className="case-number-text">{caseItem.caseNumber}</span>
              {isReOutsource && caseItem.originalCaseNumber && (
                <span
                  className="original-case-badge"
                  title={`This is a re-outsource of original case #${caseItem.originalCaseNumber}`}
                >
                  orig: {caseItem.originalCaseNumber}
                </span>
              )}
            </div>
            {unread && <span className="unread-dot" />}
          </td>
          <td className={`date-cell ${unread ? 'cell-unread' : ''}`}>
            {formatDate(caseItem.createdAt)}
          </td>
          <td className={`lab-cell ${unread ? 'cell-unread' : 'cell-normal'}`}>
            {caseItem.destinationLab?.labName || 'N/A'}
          </td>
          <td className="procedures-cell">
            {procedureCount > 1 ? (
              <button
                onClick={() => handleShowProcedures(caseItem)}
                className={`procedures-button ${unread ? 'procedures-button-unread' : 'procedures-button-read'}`}
              >
                <FaList /> {procedureCount} Procedures
              </button>
            ) : (
              <span className={`procedure-name ${unread ? 'cell-unread' : 'cell-normal'}`}>
                {procedures[0]?.procedureName || 'N/A'}
              </span>
            )}
          </td>
          <td className={`date-cell ${unread ? 'cell-unread' : ''}`}>
            {formatDate(caseItem.requiredDate)}
          </td>
          <td className="status-cell">
            <span className="status-text" style={{ color: displayStatusStyle.color }}>
              {displayStatusStyle.icon}
              {getStatusDisplayText()}
            </span>
          </td>
          <td className="view-status-cell">
            {renderViewStatus(caseItem)}
          </td>
          <td className="invoice-cell">
            {caseItem.invoiceNumber ? (
              hasPricingAccess ? (
                <a
                  href={`/outsource-invoice/${caseItem.invoiceNumber}`}
                  onClick={(e) => handleViewInvoice(caseItem.invoiceNumber, e)}
                  className={`invoice-link ${unread ? 'invoice-link-unread' : ''}`}
                >
                  {caseItem.invoiceNumber}
                </a>
              ) : (
                <span className="no-invoice" style={{ color: '#6c757d' }}>
                  {caseItem.invoiceNumber}
                </span>
              )
            ) : (
              <span className="no-invoice">No invoice</span>
            )}
          </td>
          <td className="action-cell">
            {!isReOutsource && (
              <Button
                variant="outline-info"
                size="sm"
                className="view-case-btn"
                onClick={() => handleViewCase(caseItem)}
              >
                View Case
              </Button>
            )}
            <button
              onClick={() => handleViewOutsource(caseItem)}
              className={`view-outsource-btn ${unread ? 'view-outsource-btn-unread' : ''}`}
            >
              <FaEye /> View Outsource
            </button>
          </td>
        </tr>
      );
    });
  }, [
    paginatedCases,
    viewInfoMap,
    getProceduresArray,
    isCaseUnread,
    getEffectiveStatus,
    isCaseEffectivelyInProgress,
    hasPricingAccess
  ]);

  // Guard: wait for pricing flag before painting
  if (pricingLoading) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="loading-container">
          <Spinner animation="border" style={{ color: '#004aad' }} />
          <p className="loading-text">Loading...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Navvbar />

      <div className="to-outsource-container">
        <div className="to-outsource-header">
          <div className="header-title-section">
            <h2 className="page-title">
              Cases Sent for Outsourcing
              {isMonthFilterActive && (
                <span className="month-label">
                  ({formatMonthYear(selectedMonth)})
                </span>
              )}
              {hasPricingAccess && (
                <span className="total-amount">
                  ({formatCurrency(statusCounts.totalAllAmount)})
                </span>
              )}
            </h2>
            {activeStatusFilter !== 'all' && (
              <p className="filter-indicator">
                Showing {activeStatusFilter === 'in_progress' ? 'in progress (including viewed pending)' : activeStatusFilter.replace('_', ' ')} cases
                <button
                  className="clear-status-filter-btn"
                  onClick={() => setActiveStatusFilter('all')}
                >
                  Clear Status Filter
                </button>
              </p>
            )}
          </div>

          <div className="header-controls">
            <div className="month-navigation">
              <button className="month-nav-btn" onClick={goToPreviousMonth} title="Previous Month">
                <LuChevronLeft size={20} />
              </button>
              <span className="month-display">{formatMonthYear(selectedMonth)}</span>
              <button className="month-nav-btn" onClick={goToNextMonth} title="Next Month">
                <LuChevronRight size={20} />
              </button>
            </div>

            <div className="action-buttons">
              {isMonthFilterActive && (
                <button onClick={resetMonthFilter} className="reset-filter-btn">
                  Reset Filter
                </button>
              )}

              <button className="export-btn export-pdf-btn" onClick={exportToPDF} title="Download PDF">
                <FaFilePdf /> PDF
              </button>
              <button className="export-btn export-excel-btn" onClick={exportToExcel} title="Download Excel">
                <FaFileExcel /> Excel
              </button>

              <button className="refresh-btn" onClick={handleRefresh}>
                <FaSync /> Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="search-bar-wrapper">
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search by Case Number or Procedure Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={clearSearch} className="clear-search-btn" title="Clear search">
                <FaTimes />
              </button>
            )}
          </div>
          {debouncedSearch && (
            <p className="search-results-info">
              Searching for: <strong>"{debouncedSearch}"</strong>
              {filteredCases.length > 0 && ` — ${filteredCases.length} result${filteredCases.length !== 1 ? 's' : ''} found`}
            </p>
          )}
        </div>

        <div className="status-cards-grid">
          {statusCards.map((card) => {
            const isActive = activeStatusFilter === card.key;
            return (
              <div
                key={card.key}
                onClick={() => handleStatusFilter(card.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStatusFilter(card.key);
                  }
                }}
                className={`status-card ${isActive ? 'status-card-active' : ''}`}
                style={{
                  backgroundColor: isActive ? card.activeTint : '#ffffff',
                  border: isActive ? `1px solid ${card.accent}` : '1px solid #e9ecef',
                  boxShadow: isActive
                    ? `0 4px 14px ${card.accent}26`
                    : '0 1px 3px rgba(0,0,0,0.04)'
                }}
              >
                <div
                  className="status-card-accent"
                  style={{
                    backgroundColor: card.accent,
                    opacity: isActive ? 1 : 0.35
                  }}
                />
                <div
                  className="status-icon"
                  style={{
                    backgroundColor: card.tint,
                    color: card.accent,
                    border: `1px solid ${card.accent}33`
                  }}
                >
                  {card.icon}
                </div>
                <div className="status-content">
                  <div className="status-numbers">
                    <span className="status-count">{card.count}</span>
                    {hasPricingAccess && (
                      <span className="status-amount" style={{ color: card.accent }}>
                        {formatCurrency(card.amount)}
                      </span>
                    )}
                  </div>
                  <span
                    className="status-label"
                    style={{
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? card.accent : '#6c757d'
                    }}
                  >
                    {card.label}
                    {isMonthFilterActive && (
                      <span className="status-label-filtered">(filtered)</span>
                    )}
                  </span>
                </div>
                {isActive && (
                  <div
                    className="active-badge"
                    style={{
                      color: card.accent,
                      border: `1px solid ${card.accent}55`
                    }}
                  >
                    <FaCheck size={9} /> Filtered
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner animation="border" style={{ color: '#004aad' }} />
            <p className="loading-text">
              {isMonthFilterActive
                ? `Loading sent cases for ${formatMonthYear(selectedMonth)}...`
                : 'Loading all sent cases...'}
            </p>
          </div>
        )}

        {error && !loading && (
          <div className="error-container">
            <p className="error-text">Error: {error}</p>
            <Button variant="outline-danger" onClick={handleRefresh}>Try Again</Button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="table-responsive">
              <table className="outsource-table">
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Created</th>
                    <th>Destination Lab</th>
                    <th>Procedures</th>
                    <th>Required Date</th>
                    <th>Status</th>
                    <th>View Status</th>
                    <th>Invoice</th>
                    <th className="th-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.length > 0 ? (
                    tableRows
                  ) : (
                    <tr>
                      <td colSpan={9} className="no-results-cell">
                        {debouncedSearch ? (
                          <>
                            No cases found matching "<strong>{debouncedSearch}</strong>"
                            {isMonthFilterActive && ` in ${formatMonthYear(selectedMonth)}`}
                          </>
                        ) : (isMonthFilterActive || activeStatusFilter !== 'all') ? (
                          <>
                            No outsource cases found
                            {isMonthFilterActive && ` for ${formatMonthYear(selectedMonth)}`}
                            {activeStatusFilter !== 'all' && ` with status: ${activeStatusFilter.replace('_', ' ')}`}
                          </>
                        ) : (
                          'No outsource cases sent yet'
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination-container">
                <Button variant="outline-secondary" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} size="sm">First</Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} size="sm">Previous</Button>
                <Button variant="primary" disabled size="sm" className="page-info-btn">Page {currentPage} of {totalPages}</Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} size="sm">Next</Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} size="sm">Last</Button>
              </div>
            )}

            <div className="results-summary">
              Showing {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''}
              {isMonthFilterActive && ` for ${formatMonthYear(selectedMonth)}`}
              {activeStatusFilter !== 'all' && ` with status: ${activeStatusFilter.replace('_', ' ')}`}
              {debouncedSearch && ` matching "${debouncedSearch}"`}
            </div>
          </>
        )}

        <Modal show={showProceduresModal} onHide={handleCloseProceduresModal} size="lg" centered>
          <Modal.Header closeButton className="procedures-modal-header">
            <Modal.Title className="procedures-modal-title">
              {selectedCaseProcedures && (
                <>
                  Procedures for Case #{selectedCaseProcedures.caseNumber}
                  {selectedCaseProcedures.isReOutsource && selectedCaseProcedures.originalCaseNumber && (
                    <span
                      style={{
                        marginLeft: '10px',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#6c757d'
                      }}
                    >
                      (original: #{selectedCaseProcedures.originalCaseNumber})
                    </span>
                  )}
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCaseProcedures && (
              <>
                <div className="procedures-modal-info">
                  <p className="procedures-modal-info-line">
                    <strong>Destination Lab:</strong> {selectedCaseProcedures.destinationLab?.labName || 'N/A'}
                  </p>
                  <p className="procedures-modal-info-line">
                    <strong>Required Date:</strong> {formatDate(selectedCaseProcedures.requiredDate)}
                  </p>
                  <p className="procedures-modal-info-line">
                    <strong>Status:</strong> {
                      (() => {
                        const viewInfo = viewInfoMap[selectedCaseProcedures.caseNumber];
                        const authoritativeStatus = viewInfo?.status || selectedCaseProcedures.status;
                        if (authoritativeStatus === 'pending' && isCaseEffectivelyInProgress(selectedCaseProcedures)) {
                          return 'IN PROGRESS';
                        }
                        return (authoritativeStatus || 'PENDING').toUpperCase().replace('_', ' ');
                      })()
                    }
                  </p>
                </div>

                <div className="procedures-table-wrapper">
                  <table className="procedures-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Procedure Name</th>
                        {hasPricingAccess && <th className="th-right">Price</th>}
                        <th className="th-center">Quantity</th>
                        {hasPricingAccess && <th className="th-right">Total Price</th>}
                        <th>Shared By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getProceduresArray(selectedCaseProcedures).map((procedure, index) => (
                        <tr key={procedure._id || index}>
                          <td>{index + 1}</td>
                          <td>{procedure.procedureName || 'N/A'}</td>
                          {hasPricingAccess && (
                            <td className="td-right">{formatCurrency(procedure.price)}</td>
                          )}
                          <td className="td-center">{procedure.quantity || 1}</td>
                          {hasPricingAccess && (
                            <td className="td-right td-bold">
                              {formatCurrency(procedure.totalPrice || (procedure.price * (procedure.quantity || 1)))}
                            </td>
                          )}
                          <td className="td-muted">{procedure.sharedByName || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                    {hasPricingAccess && (
                      <tfoot>
                        <tr className="procedures-total-row">
                          <td colSpan={4} className="td-right">Grand Total:</td>
                          <td className="td-right procedures-grand-total">
                            {formatCurrency(calculateCaseTotal(selectedCaseProcedures))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="procedures-modal-footer">
            <Button variant="secondary" onClick={handleCloseProceduresModal} size="sm">Close</Button>
            {selectedCaseProcedures && (
              <Button
                variant="primary"
                size="sm"
                className="view-full-details-btn"
                onClick={() => {
                  handleCloseProceduresModal();
                  handleViewOutsource(selectedCaseProcedures);
                }}
              >
                View Full Details
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>

      <Footer />
    </>
  );
};

export default ToOutsource;